/**
 * Workspace API helper.
 * Thin wrapper around axios that injects the auth header and the
 * /api/workspace base path. Also exposes path helpers for role-aware routing.
 */
import axios from 'axios';
import { API_URL, UPLOAD_URL } from '../../api/api';
import { uploadFileInChunks, DIRECT_UPLOAD_MAX_BYTES } from '../../api/chunkedUpload';

const base = `${API_URL}/api/workspace`;

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const req = async (method, url, data) => {
  const res = await axios({
    method,
    url: `${base}${url}`,
    data,
    headers: authHeaders(),
  });
  return res.data;
};

export const wsApi = {
  // dashboard
  dashboard: () => req('get', '/dashboard'),
  users: () => req('get', '/users'),
  // Same list narrowed to the caller's own team — for filter dropdowns, which
  // should not offer people whose data the server would refuse to return.
  // Admin/HR still get everyone.
  teamUsers: () => req('get', '/users?scope=team'),

  // clients
  listClients: () => req('get', '/clients'),
  getClient: (id) => req('get', `/clients/${id}`),
  getClientDetail: (id) => req('get', `/clients/${id}/detail`),
  createClient: (body) => req('post', '/clients', body),
  updateClient: (id, body) => req('put', `/clients/${id}`, body),
  deleteClient: (id) => req('delete', `/clients/${id}`),
  addPricing: (id, body) => req('post', `/clients/${id}/pricing`, body),

  // projects
  listProjects: (qs = '') => req('get', `/projects${qs}`),
  getProject: (id) => req('get', `/projects/${id}`),
  createProject: (body) => req('post', '/projects', body),
  updateProject: (id, body) => req('put', `/projects/${id}`, body),
  deleteProject: (id) => req('delete', `/projects/${id}`),
  projectTasks: (id) => req('get', `/projects/${id}/tasks`),
  projectProfitability: (id) => req('get', `/projects/${id}/profitability`),
  saveProjectDescription: (id, doc) => req('put', `/projects/${id}/description`, { doc }),

  // project members (manage)
  addProjectMember: (id, body) => req('post', `/projects/${id}/members`, body),
  updateProjectMember: (id, userId, body) => req('patch', `/projects/${id}/members/${userId}`, body),
  removeProjectMember: (id, userId) => req('delete', `/projects/${id}/members/${userId}`),

  // project files
  listProjectFiles: (id) => req('get', `/projects/${id}/files`),
  uploadProjectFile: async (id, file, source = 'upload', onProgress) => {
    // Under the proxy's body limit, keep the original single POST — the path
    // every small upload has always taken.
    if (file.size <= DIRECT_UPLOAD_MAX_BYTES) {
      const form = new FormData();
      form.append('file', file);
      form.append('source', source);
      const res = await axios.post(`${base}/projects/${id}/files`, form, { headers: authHeaders() });
      onProgress?.(1);
      return res.data;
    }
    // Over it, the file goes up in slices first (see api/chunkedUpload) and
    // this only claims the finished upload — a tiny JSON body.
    const uploadId = await uploadFileInChunks(file, (p) => onProgress?.(p * 0.98));
    const res = await axios.post(
      `${base}/projects/${id}/files/complete`,
      { uploadId, source },
      { headers: authHeaders() },
    );
    onProgress?.(1);
    return res.data;
  },
  deleteProjectFile: (id, fileId) => req('delete', `/projects/${id}/files/${fileId}`),

  // teams
  listTeams: () => req('get', '/teams'),
  getTeam: (id) => req('get', `/teams/${id}`),
  createTeam: (body) => req('post', '/teams', body),
  updateTeam: (id, body) => req('put', `/teams/${id}`, body),
  deleteTeam: (id) => req('delete', `/teams/${id}`),

  // overview hours graph
  hoursGraph: (qs = '') => req('get', `/hours-graph${qs}`),

  // tasks
  myTasks: (qs = '') => req('get', `/tasks/my${qs}`),
  allTasks: (qs = '') => req('get', `/tasks/all${qs}`),
  getTask: (id) => req('get', `/tasks/${id}`),
  createTask: (body) => req('post', '/tasks', body),
  updateTask: (id, body) => req('put', `/tasks/${id}`, body),
  updateTaskStatus: (id, status, position = 0) =>
    req('patch', `/tasks/${id}/status`, { status, position }),
  deleteTask: (id) => req('delete', `/tasks/${id}`),
  addComment: (id, comment) => req('post', `/tasks/${id}/comments`, { comment }),

  // attachments
  // One file per call; returns the updated task. Same split as
  // uploadProjectFile: small files in one POST, larger ones in slices so they
  // clear the proxy's ~1 MB body limit.
  uploadAttachment: async (taskId, file, onProgress) => {
    if (file.size <= DIRECT_UPLOAD_MAX_BYTES) {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${base}/tasks/${taskId}/attachments`, form, { headers: authHeaders() });
      onProgress?.(1);
      return res.data;
    }
    const uploadId = await uploadFileInChunks(file, (p) => onProgress?.(p * 0.98));
    const res = await axios.post(
      `${base}/tasks/${taskId}/attachments/complete`,
      { uploadId },
      { headers: authHeaders() },
    );
    onProgress?.(1);
    return res.data;
  },
  deleteAttachment: (id) => req('delete', `/attachments/${id}`),

  // Week-offs + holidays for the caller → { working_weekdays, weekoffs,
  // holidays: { 'YYYY-MM-DD': name } }. Fetched once so the log form can flag a
  // non-working date offline as the user types.
  nonWorkingDays: (qs = '') => req('get', `/non-working-days${qs}`),

  // time
  listTimeEntries: (qs = '') => req('get', `/time-entries${qs}`),
  addTimeEntry: (body) => req('post', '/time-entries', body),
  updateTimeEntry: (id, body) => req('put', `/time-entries/${id}`, body),
  setTimeEntryStatus: (id, status) => req('patch', `/time-entries/${id}/status`, { status }),
  deleteTimeEntry: (id) => req('delete', `/time-entries/${id}`),
  // Clockify Detailed Report (.xlsx/.csv) → { inserted, skippedDuplicate,
  // skippedUnmatched, errors[] }. tz_offset is minutes east of UTC, so the
  // server reads the report's wall-clock times in this browser's zone.
  importTimeEntries: async (file) => {
    const form = new FormData();
    form.append('file', file);
    form.append('tz_offset', String(-new Date().getTimezoneOffset()));
    const res = await axios.post(`${base}/time-entries/import`, form, { headers: authHeaders() });
    return res.data;
  },

  // project budget burn-down
  projectBudget: (id) => req('get', `/projects/${id}/budget`),

  // live timer
  getTimer: () => req('get', '/timer'),
  startTimer: (body) => req('post', '/timer/start', body),
  // `description` (optional) saves the running timer's note in the same call.
  stopTimer: (body) => req('post', '/timer/stop', body),
  pauseTimer: () => req('post', '/timer/pause'),
  resumeTimer: () => req('post', '/timer/resume'),
  updateTimer: (body) => req('patch', '/timer', body),

  // analytics
  analytics: (qs = '') => req('get', `/analytics${qs}`),

  // team cost + capacity
  listTeamCost: () => req('get', '/team-cost'),
  updateTeamCost: (userId, patch) =>
    req('put', `/team-cost/${userId}`, typeof patch === 'object' ? patch : { hourly_cost: patch }),

  // generic image upload (logos) → { url }
  uploadImage: async (file) => {
    const form = new FormData();
    form.append('file', file);
    const res = await axios.post(`${base}/upload`, form, { headers: authHeaders() });
    return res.data;
  },
};

// ── Jira (PARKED — waiting on the Jira site URL + API token) ─────────────────
// The /api/jira routes are commented out in server.js, so these calls would
// 404. Uncomment this block together with the Jira Project Key field in
// ProjectsList.js.
//
// Mounted at /api/jira, not under /api/workspace, so it needs its own base.
// Credentials live only in the backend .env — nothing Jira-authenticated is
// ever called from the browser.
// const jiraBase = `${API_URL}/api/jira`;
//
// const jiraReq = async (method, url, data) => {
//   const res = await axios({ method, url: `${jiraBase}${url}`, data, headers: authHeaders() });
//   return res.data;
// };
//
// export const jiraApi = {
//   // key is the PROJECT key ("CR") → { key, name, linked_project }
//   lookupProject: (key) => jiraReq('get', `/project/${encodeURIComponent(String(key).trim())}`),
//   // Note: there is intentionally no sync call here. Worklogs arrive by webhook
//   // (POST /webhooks/jira) and a backend sweep quietly covers missed events, so
//   // the UI never triggers a sync. Backfills are an ops call to
//   // POST /api/jira/sync?days=N, not a button.
// };

// Asset URL helper for attachments (served from backend /uploads).
// Uses UPLOAD_URL, not API_URL: in production the API is proxied at the web
// root (/api/*) while static uploads only exist under the /backend prefix.
// Building these off API_URL yields /uploads/... which Apache 404s.
export const assetUrl = (p) => (p && p.startsWith('http') ? p : `${UPLOAD_URL}${p}`);

export const canManageCost = () =>
  (JSON.parse(localStorage.getItem('permissions') || '[]')).includes('manage_employee_cost');

// Role-aware base path: /admin/workspace, /hr/workspace, or /member/workspace
export const wsBase = () => {
  const role = localStorage.getItem('role');
  if (role === 'Admin') return '/admin/workspace';
  if (role === 'HR') return '/hr/workspace';
  return '/member/workspace';
};

// There is deliberately no "is Admin or HR" helper here: workspace access is
// decided by Roles & Access keys (hasPermission), exactly as the server decides
// it, so a custom role such as "Manager" can be granted any of it.
//
// True Admin only. Kept for a genuinely Admin-exclusive capability, should one
// ever exist — gate features on a permission key instead.
// Case-insensitive: the role has been stored both ways over time.
export const isAdmin = () =>
  String(localStorage.getItem('role') || '').toLowerCase() === 'admin';
